// ─── Types ────────────────────────────────────────────────────────────────────

export type StorePriceRecord = {
  storeName:  string
  price:      number
  recordedAt: string
}

export type ItemPriceComparison = {
  itemId:              string
  itemName:            string
  itemEmoji:           string
  stores: {
    storeName:    string
    avgPrice:     number
    lowestPrice:  number
    visitCount:   number
    lastSeen:     string
  }[]
  cheapestStore:      string
  mostExpensiveStore: string
  maxSaving:          number
}

export type HouseholdSavingsOpportunity = {
  item:                   ItemPriceComparison
  potentialMonthlySaving: number
  currentStore:           string
  cheaperStore:           string
  priceDiff:              number
}

// ─── analyzeStorePrices ───────────────────────────────────────────────────────

export function analyzeStorePrices(storePrices: any[]): ItemPriceComparison[] {
  // Group by item_id
  const byItem: Record<string, any[]> = {}
  for (const record of storePrices) {
    if (!byItem[record.item_id]) byItem[record.item_id] = []
    byItem[record.item_id].push(record)
  }

  const comparisons: ItemPriceComparison[] = []

  for (const [itemId, records] of Object.entries(byItem)) {
    if (records.length === 0) continue

    const item = records[0]

    // Group by store
    const byStore: Record<string, number[]> = {}
    for (const record of records) {
      if (!record.store_name) continue
      if (!byStore[record.store_name]) byStore[record.store_name] = []
      byStore[record.store_name].push(record.price)
    }

    // Only include items with 2+ stores
    if (Object.keys(byStore).length < 2) continue

    const stores = Object.entries(byStore)
      .map(([storeName, prices]) => ({
        storeName,
        avgPrice:    prices.reduce((s, p) => s + p, 0) / prices.length,
        lowestPrice: Math.min(...prices),
        visitCount:  prices.length,
        lastSeen:    records
          .filter(r => r.store_name === storeName)
          .sort((a, b) =>
            new Date(b.recorded_at).getTime() - new Date(a.recorded_at).getTime()
          )[0]?.recorded_at || '',
      }))
      .sort((a, b) => a.avgPrice - b.avgPrice)

    const cheapestStore      = stores[0].storeName
    const mostExpensiveStore = stores[stores.length - 1].storeName
    const maxSaving          = stores[stores.length - 1].avgPrice - stores[0].avgPrice

    comparisons.push({
      itemId,
      itemName:  item.pantry_items?.name  || 'Unknown',
      itemEmoji: item.pantry_items?.emoji || '📦',
      stores,
      cheapestStore,
      mostExpensiveStore,
      maxSaving,
    })
  }

  return comparisons.sort((a, b) => b.maxSaving - a.maxSaving)
}

// ─── calculateSavingsOpportunities ───────────────────────────────────────────

export function calculateSavingsOpportunities(
  comparisons:         ItemPriceComparison[],
  recentConfirmations: any[],
): HouseholdSavingsOpportunity[] {
  const opportunities: HouseholdSavingsOpportunity[] = []

  for (const comparison of comparisons) {
    if (comparison.maxSaving < 0.50) continue

    // Find which store they usually buy from
    const recentForItem = recentConfirmations
      .filter(c => c.item_id === comparison.itemId && c.store_name)
      .sort((a, b) =>
        new Date(b.confirmed_at).getTime() - new Date(a.confirmed_at).getTime()
      )

    const currentStore = recentForItem[0]?.store_name || comparison.mostExpensiveStore

    if (currentStore === comparison.cheapestStore) continue

    const currentStoreData = comparison.stores.find(s => s.storeName === currentStore)
    const cheaperStoreData  = comparison.stores.find(s => s.storeName === comparison.cheapestStore)

    if (!currentStoreData || !cheaperStoreData) continue

    const priceDiff             = currentStoreData.avgPrice - cheaperStoreData.avgPrice
    const potentialMonthlySaving = priceDiff * 4

    opportunities.push({
      item: comparison,
      potentialMonthlySaving,
      currentStore,
      cheaperStore: comparison.cheapestStore,
      priceDiff,
    })
  }

  return opportunities.sort((a, b) => b.potentialMonthlySaving - a.potentialMonthlySaving)
}
